import { Component, OnDestroy } from '@angular/core';
import { BaseCustomFieldViewComponent } from '../../../base/form';
import { blank, Identifiable, observe } from '../../../shared/utils/common';
import { BehaviorSubject, merge, Subscription } from 'rxjs';
import {
  FieldmagicDataGridColumnDef,
  FieldmagicDataGridContextMenu,
  FieldmagicDataGridContextMenuParams,
  FieldmagicDataGridOptions,
} from '../../../shared/components/data-grid/data-grid.component';
import { ApiService } from '../../../services/api.service';
import { RecordService } from '../../../services/record.service';
import { NotificationService } from '../../../services/notification.service';
import { filter, switchMap } from 'rxjs/operators';

@Component({
  selector: 'jobs-view-work-locations',
  template: `
    <div class="d-flex flex-column d-flex-gap">
      <div fieldmagicGridRow>
        <div fieldmagicGridColumn="12">
          <div class="w-100 d-flex d-flex-gap justify-content-end align-items-center">
            <fieldmagic-primary-button
              variant="link"
              icon="sync-alt"
              label="Refresh"
              (click)="onRefresh()"
              [disabled]="fetching$ | async"
            >
            </fieldmagic-primary-button>
          </div>
        </div>
      </div>

      <div fieldmagicGridRow>
        <div fieldmagicGridColumn="12">
          <fieldmagic-data-grid
            [fit]="true"
            [data]="locations$ | async"
            [options]="options"
            [columns]="columns"
            [menus]="menus"
            [fetching]="fetching$ | async"
            (onReady)="onGridReady()"
          >
          </fieldmagic-data-grid>
        </div>
      </div>
    </div>
  `,
})
export class JobViewWorkLocationsComponent extends BaseCustomFieldViewComponent implements OnDestroy {
  readonly fetching$ = new BehaviorSubject<boolean>(true);
  readonly deleting$ = new BehaviorSubject<boolean>(false);

  readonly gridIsBusy$ = merge(
    this.fetching$,
    this.deleting$,
  );

  readonly locations$ = new BehaviorSubject<TaskWorkLocationData[]>([]);

  readonly options: FieldmagicDataGridOptions = {
    enable_totals: false,
  };

  readonly columns: FieldmagicDataGridColumnDef[] = [
    {
      label: 'Work ID',
      field_name: 'work_id',
      editable: false,
    },
    {
      label: 'Address',
      field_name: 'address_name',
      editable: false,
    },
  ];

  readonly menus: FieldmagicDataGridContextMenu[] = [
    {
      label: 'Delete',
      is_row_action: true,
      action: (params) => this._onDelete(params),
    }
  ];

  private _onFetchSubscription?: Subscription;
  private _onRowDeleteSubscription?: Subscription;

  constructor(
    private readonly _api: ApiService,
    private readonly _records: RecordService,
    private readonly _notifications: NotificationService,
  ) {
    super();
  }

  ngOnDestroy(): void {
    if (this._onFetchSubscription) {
      this._onFetchSubscription.unsubscribe();
    }

    if (this._onRowDeleteSubscription) {
      this._onRowDeleteSubscription.unsubscribe();
    }
  }

  onGridReady(): void {
    this._initData();
  }

  onRefresh = () => this._initData();

  private _initData(): void {
    this._onFetchSubscription = observe({
      before: () => this.fetching$.next(true),
      after: () => this.fetching$.next(false),
      observable: () => this._api.callPrivate$({
        path: '/jobs/work_locations',
        body: {
          'job_id': this.data$.value.id,
        },
        expectsNewFormat: true,
        onSuccessResponse: (response) => response.get('items'),
      })
    }).subscribe((data) => this.locations$.next(data));
  }

  private _onDelete(params: FieldmagicDataGridContextMenuParams): void {
    if (params.node.group
      || blank(params.node.data)
    ) {
      return;
    }

    this._onRowDeleteSubscription = observe({
      before: () => this.deleting$.next(true),
      after: () => this.deleting$.next(false),
      observable: () => this._notifications.sendConfirmation('Are you sure you want to delete this record?').pipe(
        filter((result) => result.answer),
        switchMap(() => this._records.deleteRecord('job_work_locations', params.node.data.id)),
      )
    }).subscribe(() => this._initData());
  }
}

type TaskWorkLocationData = Identifiable & {
  work_id: string;
  address_name?: string;
};